import React, { useState } from 'react';
import {
  Inline,
  IconButton,
  IconThumb16,
  IconThumbActive16,
  IconThumbDown16,
  IconThumbDownActive16,
  getWindow,
} from '@stitch-fix/mode-react';
import { useSelectEvent, SkuSelectEvent } from '@stitch-fix/event-reporter';
import { useItemReactionFeedbackCreateMutation } from './itemReactionFeedbackMutation';
import {
  ItemReactionFeedbackCreateInput,
  ReactionFeedback,
  ItemReactionFeedbackSource,
} from '../types/definitions/graphql-schema.d';
import itemRatings from '../itemRatings';

export type FeedbackThumbProps = {
  /*
   * The skuId of the item
   */
  skuId: string;
  /*
   * The index of the item in the list of items
   */
  itemIndex: number;
  /*
   * The more-specific source of the feedback (e.g. category set id)
   */
  sourceId?: string | null | undefined;
  /*
   * The source of the feedback
   */
  source: ItemReactionFeedbackSource;
  /*
   * The page/app where the feedback is being given.
   */
  page: string;
};

export const RatingToActionName: Record<
  ReactionFeedback,
  SkuSelectEvent['action_name']
> = {
  NEUTRAL: 'remove_rating',
  LIKE: 'like',
  DISLIKE: 'dislike',
};

export const FeedbackThumbs = ({
  skuId,
  itemIndex,
  sourceId,
  source,
  page,
}: FeedbackThumbProps) => {
  const [currentRating, setCurrentRating] = useState(
    itemRatings.get(skuId) as ReactionFeedback,
  );

  const { reportSelectEvent } = useSelectEvent();

  const [itemReactionFeedbackCreateMutation] =
    useItemReactionFeedbackCreateMutation();

  const handleClick = (newRating: ReactionFeedback) => {
    itemRatings.set(skuId, newRating);
    setCurrentRating(newRating);
    const input = {
      skuId,
      reaction: newRating,
      reactedAt: new Date().toISOString(),
      context: {
        page,
        sourceId,
        source,
        sourcePosition: itemIndex,
        userAgent: getWindow()?.navigator?.userAgent,
      },
    } as ItemReactionFeedbackCreateInput;

    itemReactionFeedbackCreateMutation({
      variables: {
        input,
      },
    });

    reportSelectEvent({
      schema: 'sku_select',
      name: 'item_rating',
      action_name: RatingToActionName[newRating],
      sku_id: skuId,
    });
  };

  return (
    <Inline spacing={0.25}>
      <IconButton
        variant="overlay"
        data-testid="dislike"
        onClick={() =>
          handleClick(currentRating === 'DISLIKE' ? 'NEUTRAL' : 'DISLIKE')
        }
        aria-label={
          currentRating === 'DISLIKE' ? 'Dislike item selected' : 'Dislike item'
        }
      >
        {currentRating === 'DISLIKE' ? (
          <IconThumbDownActive16 title="Dislike item selected" />
        ) : (
          <IconThumbDown16 title="Dislike item" />
        )}
      </IconButton>
      <IconButton
        variant="overlay"
        data-testid="like"
        onClick={() =>
          handleClick(currentRating === 'LIKE' ? 'NEUTRAL' : 'LIKE')
        }
        aria-label={
          currentRating === 'LIKE' ? 'Like item selected' : 'Like item'
        }
      >
        {currentRating === 'LIKE' ? (
          <IconThumbActive16 title="Like item selected" />
        ) : (
          <IconThumb16 title="Like item" />
        )}
      </IconButton>
    </Inline>
  );
};

export default FeedbackThumbs;
